import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    AddAudienceMembership as AddAudienceMembershipMutation,
    AddAudienceMembership_addEntityToAudience_audienceMembers as AddedAudienceMembership,
    AddAudienceMembershipVariables as AddAudienceMembershipMutationVariables,
    AddEntityToAudienceInput,
    AudienceBasic,
    GetContactAudiences as GetContactAudiencesQuery,
    GetContactAudiencesVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../../../apollo-client';

import AUDIENCE_WIDGET_FRAGMENT from '../fragments/audience-widget-fragment.graphql';
import ADD_AUDIENCE_MEMBERSHIP from '../mutations/add-audience-membership.graphql';
import GET_CONTACT_AUDIENCES from '../queries/get-contact-audiences.graphql';

export const useAddAudienceMembership = (
    contactId: string
): {
    addAudienceMembership: (input: AddEntityToAudienceInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [addAudienceMembership, {loading, error}] = useMutation<
        AddAudienceMembershipMutation,
        AddAudienceMembershipMutationVariables
    >(ADD_AUDIENCE_MEMBERSHIP);

    return {
        addAudienceMembership: (input: AddEntityToAudienceInput) => {
            const optimisticAudience = nutApolloClient.readFragment<AudienceBasic>({
                id: input.audienceId,
                fragment: AUDIENCE_WIDGET_FRAGMENT,
                fragmentName: 'AudienceWidgetFragment',
            });
            const optimisticAudienceMembershipId = `temp-${Date.now()}-${Math.random()}`;

            return addAudienceMembership({
                variables: {input},
                optimisticResponse: optimisticAudience
                    ? {
                          addEntityToAudience: {
                              __typename: 'AddEntityToAudiencePayload',
                              audienceMembers: [
                                  {
                                      __typename: 'AudienceMember',
                                      id: optimisticAudienceMembershipId,
                                      audience: {
                                          __typename: 'Audience',
                                          id: optimisticAudience.id,
                                          name: optimisticAudience.name,
                                          isWebFX: optimisticAudience.isWebFX,
                                      },
                                  },
                              ],
                          },
                      }
                    : undefined,
                update: (proxy, {data}) => {
                    const cacheResult: GetContactAudiencesQuery | null | undefined =
                        proxy.readQuery<GetContactAudiencesQuery, GetContactAudiencesVariables>({
                            query: GET_CONTACT_AUDIENCES,
                            variables: {personId: contactId},
                        });
                    const addedAudienceMembership =
                        data && data.addEntityToAudience.audienceMembers[0];

                    if (cacheResult && cacheResult.contact && addedAudienceMembership) {
                        proxy.writeQuery({
                            query: GET_CONTACT_AUDIENCES,
                            variables: {personId: contactId},
                            data: {
                                ...cacheResult,
                                contact: {
                                    ...cacheResult.contact,
                                    audienceMembershipsSubscribed: [
                                        ...cacheResult.contact.audienceMembershipsSubscribed.filter(
                                            (membership) =>
                                                membership.audience.id !==
                                                addedAudienceMembership.audience.id
                                        ),
                                        {
                                            __typename: 'AudienceMember',
                                            id: addedAudienceMembership.id,
                                            audience: {
                                                __typename: 'Audience',
                                                id: addedAudienceMembership.audience.id,
                                                name: addedAudienceMembership.audience.name,
                                                isWebFX: addedAudienceMembership.audience.isWebFX,
                                            },
                                        } satisfies AddedAudienceMembership,
                                    ],
                                },
                            },
                        });
                    }
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: error && safelyGetGraphQLErrorMessage(error),
    };
};
